import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { KinesisVideoArchivedMediaClient, ListFragmentsCommand } from '@aws-sdk/client-kinesis-video-archived-media';
import { Milliseconds } from '@iotv/iotv-v3-types';
import { Stack } from '@mui/material';
import dayjs from 'dayjs';
import { Environment } from 'internal-config';
import { useContext, useEffect, useReducer, useRef } from 'react';
import { StatusIcons } from '../../../Iconography';
import { getPulseSX } from '../../../cosmetics/sx';
import { KinesisVideoStreamContext } from '../Context/KinesisVideoStreamContext';
import { Auth } from 'aws-amplify';
import utc from 'dayjs/plugin/utc';
dayjs.extend(utc);
const { region } = Environment;
const initialState = {
    isLiveIntervalTimer: null,
};
const FragmentDetectorReducer = (prev, next) => ({ ...prev, ...next });
/** Only monitors whether recent fragments indicate the stream is live, does not disptach Fragments to state as only most recent are polled */
export const useFragmentDetector = ({ checkIsLiveIntervalInMs }) => {
    const [localState, localDispatch] = useReducer(FragmentDetectorReducer, initialState);
    const { state, dispatch } = useContext(KinesisVideoStreamContext);
    const isLiveRef = useRef(state.isLive);
    const { DataEndpoint, kvaClient, selectedStreamInfo, } = state;
    useEffect(() => {
        isLiveRef.current = state.isLive;
    }, [state.isLive]);
    const buildKvaClientWithFreshCredentials = async () => {
        const credentials = await Auth.currentUserCredentials();
        const endpoint = DataEndpoint ?? undefined;
        const refreshedClient = new KinesisVideoArchivedMediaClient({ region, credentials, endpoint });
        dispatch({ kvaClient: refreshedClient });
        return refreshedClient;
    };
    const listFragmentsInternal = async ({ selectedStreamInfo, TimestampRange, NextToken, MaxResults, }, clientOverride, retryCount = 0) => {
        const fragments = [];
        let newNextToken = undefined;
        const client = clientOverride ?? kvaClient;
        if (selectedStreamInfo && client) {
            if (client && selectedStreamInfo) {
                const params = {
                    StreamARN: selectedStreamInfo.StreamARN,
                    FragmentSelector: {
                        FragmentSelectorType: 'PRODUCER_TIMESTAMP',
                        TimestampRange,
                    },
                    NextToken,
                    MaxResults,
                };
                try {
                    const { Fragments, NextToken } = await client.send(new ListFragmentsCommand(params));
                    if (NextToken && MaxResults !== 1) {
                        console.log(`listFragmentsInternal needs iteration`, !!NextToken);
                        newNextToken = NextToken;
                    }
                    if (Fragments && Fragments.length > 0) {
                        console.log(`listFragmentsInternal returned ${Fragments.length} fragments`, Fragments);
                        fragments.push(...Fragments);
                    }
                    else {
                        console.log(`listFragmentsInternal returned no fragments`);
                    }
                }
                catch (e) {
                    const { message, name } = e;
                    console.error(`listFragmentsInternal ${name} ${message}`);
                    switch (name) {
                        case 'ResourceNotFoundException':
                            break;
                        case 'NotAuthorizedException': {
                            if (retryCount >= 2) {
                                console.error(`listFragmentsInternal ${name} exhausted retries`);
                                break;
                            }
                            const refreshedClient = await buildKvaClientWithFreshCredentials();
                            return listFragmentsInternal({ selectedStreamInfo, TimestampRange, NextToken, MaxResults }, refreshedClient, retryCount + 1);
                            break;
                        }
                        case 'ExpiredTokenException': {
                            console.log(`ExpiredTokenException: refreshing credentials and retrying...`);
                            if (retryCount >= 2) {
                                console.error(`ExpiredTokenException: retries exhausted`);
                                break;
                            }
                            const refreshedClient = await buildKvaClientWithFreshCredentials();
                            return listFragmentsInternal({ selectedStreamInfo, TimestampRange, NextToken, MaxResults }, refreshedClient, retryCount + 1);
                            break;
                        }
                        default:
                            console.error(`listFragmentsInternal ${name} ${message}`);
                    }
                }
            }
            else {
                console.error(`listFragmentsInternal: kvaClient or selectedStreamInfo is undefined`);
            }
        }
        else {
            console.error(`listFragmentsInternal: selectedStreamInfo or kvaClient is undefined`);
        }
        return { Fragments: fragments, NextToken: newNextToken };
    };
    useEffect(() => {
        if (selectedStreamInfo?.StreamARN && kvaClient) {
            if (localState.isLiveIntervalTimer) {
                console.log('Clearing previous isLiveIntervalTimer');
                clearInterval(localState.isLiveIntervalTimer);
                localDispatch({ isLiveIntervalTimer: null });
            }
            const isLiveInterval = setInterval(async () => {
                console.log(`Checking ${selectedStreamInfo?.StreamARN} for live video fragments...`);
                const liveDate = dayjs().utc().subtract(30, 'seconds').startOf('minute').toDate();
                const liveDuration = 60 * Milliseconds.SECOND;
                const liveTimestampRange = {
                    StartTimestamp: liveDate,
                    EndTimestamp: dayjs(liveDate).add(liveDuration, 'milliseconds').toDate(),
                };
                console.log('liveTimestampRange:', liveTimestampRange.StartTimestamp, liveTimestampRange.EndTimestamp);
                const fragmentsLive = await listFragmentsInternal({
                    selectedStreamInfo,
                    TimestampRange: liveTimestampRange,
                    NextToken: undefined,
                    MaxResults: 1,
                });
                const isLive = fragmentsLive.Fragments.length > 0;
                console.log('isLive:', isLive, 'fragmentsLive:', fragmentsLive.Fragments.length);
                if (isLive !== isLiveRef.current) {
                    isLiveRef.current = isLive;
                    dispatch({ isLive });
                }
            }, checkIsLiveIntervalInMs);
            localDispatch({ isLiveIntervalTimer: isLiveInterval });
        }
        else {
            console.log(`selectedStreamInfo or kvaClient is undefined (useFragmentDetector)`, selectedStreamInfo, kvaClient);
        }
        return () => {
            if (localState.isLiveIntervalTimer) {
                console.log('Clearing isLiveIntervalTimer');
                clearInterval(localState.isLiveIntervalTimer);
                localDispatch({ isLiveIntervalTimer: null });
            }
        };
    }, [selectedStreamInfo?.StreamARN, kvaClient]);
    const LiveVideoIndicator = (_jsx(Stack, { children: state.isLive ? (_jsx("span", { style: { color: 'green' }, children: _jsx(StatusIcons.Recording, { sx: { color: 'green', ...getPulseSX({ animationDuration: 1 }) } }) })) : (_jsxs("span", { style: { color: 'red' }, children: [_jsx(StatusIcons.Recording, { sx: { color: 'grey', } }), "        "] })) }));
    return {
        isLive: state.isLive,
        LiveVideoIndicator,
    };
};
